import styles from "./HeaderSection.module.css";
import { Avatar, Box, Divider } from "@justworkshr/milo-core";
import { HeaderSectionProps } from "./types";
import { useProfileContextData } from "../../contexts/ProfileInfoContext";
import HeaderStatus from "./components/HeaderStatus";
import {
  getStatusStyling,
  getMemberEventBadgeText,
  getMemberEventTooltipText,
  getTooltipDate,
} from "./utils";
import AvatarWithMap from "./components/AvatarWithMap";
import HeaderActions from "./components/HeaderActions";
import WorkLocationBadge from "./components/WorkLocationBadge";
import DateOfBirthBadge from "./components/DateOfBirthBadge";
import { SupportedCountriesContext } from "pages/employer-of-record/contexts/supportedCountriesContext";
import { useContext } from "react";
import useIsOwnProfile from "../../hooks/useIsOwnProfile";
import { useGetFeatureFlag } from "lib/launch-darkly";
import { useEorOffboardingPath } from "lib/resource-finder";
import { SystemIcon } from "@justworkshr/milo-icons";
import Tooltip from "@justworkshr/milo-tooltip";

const HeaderSection = ({
  name,
  pronouns,
  title,
  countryCode,
  dateOfBirth,
  latestMemberEvent,
  latestMemberEventCreatedAt,
  setAlert,
}: HeaderSectionProps) => {
  const isOwnProfile = useIsOwnProfile();
  const { employeeStatus } = useProfileContextData();
  const statusStyling = getStatusStyling(
    isOwnProfile ? undefined : employeeStatus,
    styles
  );
  const { getCountryName } = useContext(SupportedCountriesContext);
  const { getFeatureFlag } = useGetFeatureFlag();
  const neonOffboardingPath = useEorOffboardingPath();

  const isOnboardingStatusBadgeEnabled = getFeatureFlag(
    "release-eor-ee-profile-onboarding-visibility"
  );

  function onRequestTermination() {
    window.location.assign(neonOffboardingPath);
  }

  return (
    <>
      {isOwnProfile && (
        <>
          <div className={styles.title}>Your account settings</div>
          <Divider />
        </>
      )}
      <div className={`${styles.headerBoxWrapper} ${statusStyling}`}>
        {employeeStatus && !isOwnProfile && <HeaderStatus />}
        <Box padding="lg">
          <div className={styles.headerBoxContent}>
            {isOwnProfile && <Avatar size="xl" name={name} />}
            {!isOwnProfile && (
              <AvatarWithMap name={name} countryCode={countryCode}>
                <HeaderActions
                  onRequestTermination={onRequestTermination}
                  setAlert={setAlert}
                />
              </AvatarWithMap>
            )}

            <div
              className={
                isOwnProfile ? styles.pageHeaderWithoutMap : styles.pageHeader
              }
            >
              <div className={styles.employeeInfo}>
                <div className={styles.employeeNameWrapper}>
                  <p className={styles.employeeName}>{name}</p>
                  {isOnboardingStatusBadgeEnabled &&
                    !isOwnProfile &&
                    latestMemberEvent &&
                    getMemberEventBadgeText(latestMemberEvent) && (
                      <Tooltip
                        color="neutral"
                        position="top"
                        title={
                          (
                            <>
                              <p className={styles.memberEventTooltipText}>
                                {getMemberEventTooltipText(
                                  latestMemberEvent,
                                  name
                                )}
                              </p>
                              <p className={styles.memberEventTooltipDate}>
                                Last updated{" "}
                                {getTooltipDate(latestMemberEventCreatedAt)}
                              </p>
                            </>
                          ) as unknown as string
                        }
                        data-testid="tooltip"
                      >
                        <div className={styles.memberEventBadge}>
                          <p className={styles.memberEventBadgeText}>
                            {getMemberEventBadgeText(latestMemberEvent)}
                          </p>
                          <SystemIcon
                            iconName="help"
                            size="extra-small"
                            color="neutral-subtle"
                            data-testid="help-icon"
                          />
                        </div>
                      </Tooltip>
                    )}
                </div>
                {pronouns && <p className={styles.pronouns}>{pronouns}</p>}
                <div title={styles.jobTitle}>{title}</div>
              </div>

              <div className={styles.geoInfoWrapper}>
                <WorkLocationBadge countryName={getCountryName(countryCode)} />
                {dateOfBirth && <DateOfBirthBadge dateOfBirth={dateOfBirth} />}
              </div>
            </div>
          </div>
        </Box>
      </div>
    </>
  );
};

export default HeaderSection;
